/**
 * 앱 전체 키보드 단축키의 단일 목록. 리스너와 `?` 도움말이 둘 다 이 목록을 읽어서
 * 도움말에 적힌 키와 실제로 동작하는 키가 어긋날 수 없다.
 *
 * 규칙 (gbrain fact #3050, 2026-10-02):
 * - 물리 키(`e.code`)로 판정한다. 한글 입력 상태에서 W 키의 `e.key` 는 'ㅈ' 이다.
 * - 한글 조합 중(`isComposing`)이거나 안쪽 층이 이미 처리한(`defaultPrevented`) 키는 건너뛴다.
 * - 수식키 없는 키는 입력 중에 무시한다. Ctrl·Alt 조합은 입력 중에도 동작한다.
 * - 창은 Esc 로 닫지 않는다. Esc 는 팝오버·모달 몫이다.
 * - 표기는 주 사용자가 Windows 라 Ctrl·Alt 로 고정한다. 오른쪽 Alt 는 한국어 자판의 한/영 키라
 *   왼쪽 Alt 로만 동작한다.
 */

export interface ShortcutInfo {
  /** KbdHint 표기 — 여러 키는 '+' 로 잇는다. */
  keys: string;
  label: string;
}

export interface Shortcut extends ShortcutInfo {
  match: (e: KeyboardEvent) => boolean;
}

const noMeta = (e: KeyboardEvent) => !e.metaKey;

export const WINDOW_SHORTCUTS = {
  nextWindow: {
    keys: 'Ctrl+`',
    label: '다음 창으로 옮겨 갑니다.',
    match: (e) => e.ctrlKey && !e.altKey && !e.shiftKey && noMeta(e) && e.code === 'Backquote',
  },
  prevWindow: {
    keys: 'Ctrl+Shift+`',
    label: '이전 창으로 옮겨 갑니다.',
    match: (e) => e.ctrlKey && !e.altKey && e.shiftKey && noMeta(e) && e.code === 'Backquote',
  },
  closeWindow: {
    keys: 'Alt+W',
    label: '포커스가 있는 창을 닫습니다.',
    match: (e) => e.altKey && !e.ctrlKey && !e.shiftKey && noMeta(e) && e.code === 'KeyW',
  },
  minimizeWindow: {
    keys: 'Alt+M',
    label: '포커스가 있는 창을 최소화합니다.',
    match: (e) => e.altKey && !e.ctrlKey && !e.shiftKey && noMeta(e) && e.code === 'KeyM',
  },
  help: {
    keys: '?',
    label: '이 도움말을 엽니다.',
    match: (e) =>
      !e.ctrlKey && !e.altKey && noMeta(e) && (e.key === '?' || (e.shiftKey && e.code === 'Slash')),
  },
} satisfies Record<string, Shortcut>;

/** 창 안 화면이 따로 받는 키 — 그 창에 포커스가 있을 때 도움말 아래칸에 보인다. 키는 창 id. */
export const SCREEN_SHORTCUTS: Record<string, ShortcutInfo[]> = {
  'advertiser-console': [
    { keys: 'N', label: '새 광고주를 등록합니다.' },
    { keys: '/', label: '광고주 검색칸으로 갑니다.' },
    { keys: '↑ ↓', label: '목록에서 위아래로 옮겨 갑니다.' },
    { keys: 'Enter', label: '목록에서 광고주를 고릅니다.' },
    { keys: 'Esc', label: '검색칸에서 검색어를 지웁니다.' },
  ],
  wiki: [{ keys: '/', label: '위키 검색칸으로 갑니다.' }],
  'admin-attendance': [{ keys: '/', label: '집계 표 검색칸으로 갑니다. Ctrl+K 도 됩니다.' }],
};

export function shouldSkipShortcut(e: KeyboardEvent): boolean {
  return e.isComposing || e.defaultPrevented;
}

/** 모달이 열려 있으면 창 키가 모달 밖 창을 건드리지 않는다 — 창 안에 그려진 모달도 있다. */
export function isModalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.closest('input, textarea, select, [contenteditable="true"]') !== null
  );
}

/** 최소화되지 않은 창을 연 순서대로 돈다. 포커스된 창이 없으면 첫 창. */
export function nextWindowId(
  openWindows: readonly string[],
  minimizedWindows: readonly string[],
  current: string | null,
  direction: 1 | -1
): string | null {
  const visible = openWindows.filter((id) => !minimizedWindows.includes(id));
  if (visible.length === 0) return null;
  const index = current ? visible.indexOf(current) : -1;
  if (index === -1) return visible[0] ?? null;
  return visible[(index + direction + visible.length) % visible.length] ?? null;
}
